import React from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const TrainingPlatformUseCase = () => {
  const keyCapabilities = [
    "Real hardware access: AURIX, Orin, NXP GoldBox in your browser",
    "Professional tooling: TRACE32, ETAS INCA, Eclipse IDE with AI assistants",
    "Measurable ROI: 90% faster onboarding, reduced CapEx, auditable progress"
  ];

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
      <div className="max-w-7xl mx-auto">
        <div 
          className="rounded-xl p-8 md:p-12"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column - Text Content */}
            <div className="flex flex-col justify-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
                style={{ backgroundColor: 'rgba(245, 203, 92, 0.15)', border: '1px solid rgba(245, 203, 92, 0.4)', color: '#F5CB5C' }}
              >
                Training Platform
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#F5CB5C' }}>
                Sleeve Training Platform
              </h2>

              <p className="text-base leading-relaxed mb-8" style={{ color: '#9CA3AF' }}>
                On-demand access to real embedded hardware and professional tooling through your browser. Train engineers on $50K+ lab equipment instantly—no setup, no travel, no hardware bottlenecks.
              </p>

              <div className="mb-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
                  Key Capabilities
                </h3>
                <ul className="space-y-4">
                  {keyCapabilities.map((capability, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: '#4FB3B8' }}
                      ></div>
                      <span className="text-base" style={{ color: '#E8EDDF' }}>{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link 
                to="/use-cases/training-platform"
                className="inline-flex items-center gap-2 text-base font-medium transition-colors duration-250 w-fit"
                style={{ color: '#4FB3B8' }}
              >
                View Training Platform
                <Icon name="ArrowRight" size={16} style={{ color: '#4FB3B8' }} />
              </Link>
            </div>

            {/* Right Column - Diagram */}
            <div className="flex items-center justify-center">
              <div 
                className="w-full rounded-lg p-4 relative overflow-hidden"
                style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
              >
                <svg width="100%" height="400" viewBox="0 0 400 400" className="max-w-full relative z-10">
                  
                  {/* Browser Window - Outer gradient border effect */}
                  <defs>
                    <linearGradient id="browserGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#4FB3B8" />
                      <stop offset="100%" stopColor="#4FB3B8" />
                    </linearGradient>
                  </defs>
                  
                  {/* Browser Window */}
                  <rect x="80" y="20" width="240" height="220" rx="8" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Browser Chrome - Three dots */}
                  <circle cx="100" cy="40" r="5" fill="#4FB3B8" />
                  <circle cx="118" cy="40" r="5" fill="#4FB3B8" />
                  <circle cx="136" cy="40" r="5" fill="#4FB3B8" />
                  
                  {/* Browser Content Area */}
                  <rect x="95" y="55" width="210" height="170" rx="4" fill="transparent" stroke="#3A3A38" strokeWidth="1" />
                  
                  {/* TRACE32 Box */}
                  <rect x="105" y="65" width="190" height="40" rx="4" fill="#242423" stroke="#F5CB5C" strokeWidth="1.5" />
                  <text x="200" y="90" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="500">TRACE32</text>
                  
                  {/* TERMINAL Box */}
                  <rect x="105" y="115" width="190" height="40" rx="4" fill="#242423" stroke="#F5CB5C" strokeWidth="1.5" />
                  <text x="200" y="140" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="500">TERMINAL</text>
                  
                  {/* ECLIPSE IDE Box */}
                  <rect x="105" y="165" width="190" height="40" rx="4" fill="#242423" stroke="#F5CB5C" strokeWidth="1.5" />
                  <text x="200" y="190" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="500">ECLIPSE IDE</text>
                  
                  {/* Connection lines from browser to hardware */}
                  <line x1="200" y1="240" x2="200" y2="270" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="110" y1="270" x2="290" y2="270" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Vertical lines to hexagons */}
                  <line x1="110" y1="270" x2="110" y2="300" stroke="#9CA3AF" strokeWidth="1.5" />
                  <line x1="200" y1="270" x2="200" y2="300" stroke="#9CA3AF" strokeWidth="1.5" />
                  <line x1="290" y1="270" x2="290" y2="300" stroke="#9CA3AF" strokeWidth="1.5" />
                  
                  {/* AURIX Hexagon */}
                  <polygon 
                    points="110,300 70,320 70,355 110,375 150,355 150,320" 
                    fill="#242423" 
                    stroke="#9CA3AF" 
                    strokeWidth="1.5"
                  />
                  <text x="110" y="342" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">AURIX</text>
                  
                  {/* ORIN Hexagon with warning indicator */}
                  <polygon 
                    points="200,300 160,320 160,355 200,375 240,355 240,320" 
                    fill="#242423" 
                    stroke="#9CA3AF" 
                    strokeWidth="1.5"
                  />
                  <text x="200" y="342" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">ORIN</text>
                  
                  {/* Warning triangle on ORIN */}
                  <polygon 
                    points="220,308 226,318 214,318" 
                    fill="#FF6B6B" 
                  />
                  <text x="220" y="316" textAnchor="middle" fill="#242423" fontSize="7" fontWeight="bold">!</text>
                  
                  {/* NXP GOLDBOX Hexagon */}
                  <polygon 
                    points="290,300 250,320 250,355 290,375 330,355 330,320" 
                    fill="#242423" 
                    stroke="#9CA3AF" 
                    strokeWidth="1.5"
                  />
                  <text x="290" y="336" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">NXP</text>
                  <text x="290" y="350" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">GOLDBOX</text>
                  
                </svg>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrainingPlatformUseCase;